/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { createSelector } from "@reduxjs/toolkit";
import { encode as base64Encode, decode } from "base-64";
import { AppState } from "../../";
import { toTimespanRangeMs } from "../../helpers/helpers";
import { IfilterObj } from "../types";
import { constStr } from "../../../globals/constants";
import { deviceSiteGroupSelector, deviceRegionFilterIdsSelector, deviceConfigStatusSelector } from "../Nodes/selectors";

const EMPTY_ARRAY: string[] = [];

interface IFilterObj {
  type: string;
  operator: string;
  fieldName: string;
  value: string | number;
  predicates: {}[];
}


export const eventFilterSelector = (state: AppState) => state.eventfilter;
export const eventFilterSeveritySelector = (state: AppState) => state.eventfilter.selectedEventSeverity;
export const eventFilterManagedObjectSelector = (state: AppState) => state.eventfilter.selectedManagedObject;
export const eventFilterEventTypeSelector = (state: AppState) => state.eventfilter.selectedEventType;
export const eventFilterEventTimeSelector = (state: AppState) => state.eventfilter.selectedEventTime;
export const eventFilterHostNameSelector = (state: AppState) => state.eventfilter.selectedHostName;
export const dataGridSortSelector = (state: AppState) => state.helpers.sortModel;
export const eventFilterMaintenanceModeSelector = (state: AppState) => state.eventfilter.
  selectedMaintenanceMode;
export const eventFilterParentKeySelector = (state: AppState) => state.eventfilter.parentKey;
const selectedGeoRegionSelector = (state: AppState) => state.admin.selectedGeoRegion.name || "";
const selectedGeoRegionIdsSelector = (state: AppState) => state.admin.selectedGeoRegionIds || EMPTY_ARRAY;
export const deviceKpiSyncSelector = (state: AppState) => state.filter.pmCollected;
export const deviceStatusSelector = (state: AppState) => state.filter.selectedDeviceStatus;
export const isDevicePageSelector = (state: AppState) => state.eventfilter.isDeviceDetailPage;
export const eventFilterLabelsSelector  = (state: AppState) =>  state.eventfilter.selectedLabels;
const currentPathnameSelector = () => window.location.pathname;
export const eventFilterRegionIdsSelector = (state: AppState) => state.eventfilter.regionFilterIds || EMPTY_ARRAY;


export const urlParameterConditionSelector = createSelector(
  deviceSiteGroupSelector,
  eventFilterLabelsSelector,
  eventFilterSeveritySelector,
  eventFilterManagedObjectSelector,
  eventFilterEventTypeSelector,
  eventFilterEventTimeSelector,
  eventFilterHostNameSelector,
  eventFilterMaintenanceModeSelector,
  eventFilterParentKeySelector,
  selectedGeoRegionSelector,
  selectedGeoRegionIdsSelector,
  eventFilterRegionIdsSelector,
  deviceKpiSyncSelector,
  deviceStatusSelector,
  isDevicePageSelector,
  currentPathnameSelector,
  deviceRegionFilterIdsSelector,
  deviceConfigStatusSelector,
  (siteGroup: string, labels: string[] | undefined, severity, managedObject, eventType, eventTime, hostName, maintenanceMode, parentKey, geoRegionName, geoRegionIds, regionFilterIds, pmCollected, status, isDeviceDetailPage, currentPathname, navRegionFilterIds, configstatus) => {
    const params = new URLSearchParams();
    const isDeviceDetail = isDeviceDetailPage
    // Nav panel region selection takes highest priority
    const hasNavRegionFilter = !!(navRegionFilterIds && navRegionFilterIds.length > 0);
    const effectiveRegionIds = hasNavRegionFilter ? navRegionFilterIds : (regionFilterIds && regionFilterIds.length > 0) ? regionFilterIds : geoRegionIds;
    const isEnableFilter = severity || managedObject || eventType || eventTime || hostName || maintenanceMode || parentKey || geoRegionName || (effectiveRegionIds && effectiveRegionIds.length > 0) || isDeviceDetail || configstatus;

    if (isEnableFilter) {
      // const paramsString: IfilterObj = { type: "OR", operator: "", fieldName: "", value: "", predicates: [] };
      const andPredicate: IFilterObj = { type: "AND", operator: "", fieldName: "", value: "", predicates: [] };

      if(!isDeviceDetail && !!siteGroup && siteGroup !== constStr.ungrouped){andPredicate.predicates.push({ type: "",
        operator: "EQUAL",
        fieldName: "siteGroupId",
        value: siteGroup,
        predicates: [{}]
        })}        
      

      if(!isDeviceDetail && siteGroup === constStr.ungrouped){andPredicate.predicates.push({ type: "", operator: "LIKE", fieldName: "siteGroupId", value: "99999999%", predicates: [{}] });}

      if (effectiveRegionIds && effectiveRegionIds.length > 0 && siteGroup !== constStr.ungrouped && !document.location.pathname.includes("device")){
        andPredicate.predicates.push({ type: "", operator: "IN", fieldName: "geoRegionId", value: effectiveRegionIds.join(","), predicates: [{}] });
      }

      if (severity) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "severity", value: severity, predicates: [{}] });
      }
      if (hostName) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "hostName", value: hostName, predicates: [{}] });
      }
      if (managedObject) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "managedObject", value: managedObject, predicates: [{}] });
      }
      if (labels && labels.length > 0) {
        andPredicate.predicates.push({ type: "", operator: "FIND_IN", fieldName: "labelNames", value: labels.join(","), predicates: [{}] });
      }
      if (eventType) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "eventType", value: eventType, predicates: [{}] });
      }
      if (eventTime) {
        const time = toTimespanRangeMs(eventTime);
        andPredicate.predicates.push({ type: "", operator: "GTE", fieldName: "occuredDateAndTimeMs", value: time?.from || "", predicates: [{}] });
      }
      if (maintenanceMode) {
        andPredicate.predicates.push({ type: "", operator: "is_true", fieldName: "snMaintenanceMode", value: "", predicates: [{}] });
      }
      if (parentKey) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "parentKey", value: parentKey, predicates: [{}] });
      }
      if (pmCollected) {
        andPredicate.predicates.push({ type: "", operator: "is_false", fieldName: "pmCollected", value: "", predicates: [{}] });
      }
      if (status) {
        if (status === "0" || status === 0) {
          andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "status", value: "19", predicates: [{}] });
        } else {
          andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "status", value: status, predicates: [{}] });
        }
      }
      if (configstatus) {
        andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "configurationStatus", value: configstatus, predicates: [{}] });
      }
      if (isDeviceDetail) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "parentKey", value: window.location.pathname?.split('/')[2], predicates: [{}] });
      }
      // !isDeviceDetail && paramsString.predicates.push(andPredicate);

        // const regionFilter: IfilterObj = { type: "AND", operator: "", fieldName: "", value: "", predicates: [] };
        // regionFilter.predicates.push({ type: "", operator: "EQUAL", fieldName: "parentKey", value: "Server", predicates: [{}] });

        // if (severity) {
        //   regionFilter.predicates.push({ type: "", operator: "EQ", fieldName: "severity", value: severity, predicates: [{}] });
        // }
        // if (hostName) {
        //   regionFilter.predicates.push({ type: "", operator: "EQ", fieldName: "hostName", value: hostName, predicates: [{}] });
        // }
        // if (managedObject) {
        //   regionFilter.predicates.push({ type: "", operator: "EQ", fieldName: "managedObject", value: managedObject, predicates: [{}] });
        // }
        // if (eventType) {
        //   regionFilter.predicates.push({ type: "", operator: "EQ", fieldName: "eventType", value: eventType, predicates: [{}] });
        // }
        // if (eventTime) {
        //   const time = toTimeSpanRangeMs(eventTime);
        //   regionFilter.predicates.push({ type: "", operator: "GTE", fieldName: "occuredDateAndTimeMs", value: time?.from || "", predicates: [{}] });
        // }
        // !isDeviceDetail && andPredicate.predicates.push(regionFilter)

      const base64EncodedStr = base64Encode(JSON.stringify(andPredicate));
      // if (paramsString?.predicates.length > 0) {
      //   base64EncodedStr && params.append("filters", base64EncodedStr);
      // }
      if(isDeviceDetail) {
        if (andPredicate?.predicates.length > 0) {
          base64EncodedStr && params.append("filters", base64EncodedStr);
        }
      } else {
        if (andPredicate?.predicates.length > 0) {
          andPredicate && params.append("filters", base64EncodedStr);
        }
      }
      // console.log("Device Filter :>> ", decode(base64EncodedStr));
    }

    return params;
  }
);
